const CARD_SELECTOR = '[data-hd-product-card-media]';
const SHOP_CARD_SELECTOR = '[data-hd-shop-card-media]';
const TILE_SELECTOR = '.hd-product-card__media';
const ACTIVE_SLIDE_SELECTOR = "slideshow-slide[aria-hidden='false']:not([hidden])";
const FALLBACK_SLIDE_SELECTOR = 'slideshow-slide:not([hidden])';
const IMAGE_SELECTOR = 'img.product-media__image, img';
const PRIMARY_IMAGE_SELECTOR = 'img[data-hd-card-primary-media], [data-hd-card-primary-media] img';
const SHOP_IMAGE_SELECTOR = 'img[data-hd-shop-card-image]';
const COLOR_DOTS_SELECTOR = '[data-hd-card-color-dots]';
const QUICK_ADD_SELECTOR = '.quick-add__button, .hd-product-card__quick-add-button, .hd-shop-product-card__quick-add-button';

let refreshQueued = false;

function isMobileCardMode() {
  return window.matchMedia('(max-width: 749px)').matches;
}

function getActiveSlide(card) {
  return card.querySelector(ACTIVE_SLIDE_SELECTOR) || card.querySelector(FALLBACK_SLIDE_SELECTOR);
}

function getPrimaryImage(card) {
  const image = card.querySelector(PRIMARY_IMAGE_SELECTOR);
  return image instanceof HTMLImageElement ? image : null;
}

function removePdpHoverLeak(card) {
  card.querySelectorAll('[data-hd-pdp-hover-media]').forEach((element) => {
    element.removeAttribute('data-hd-pdp-hover-media');
    element.classList.remove('is-hd-media-hovering');
  });
}

function boostImageLoad(image) {
  if (!(image instanceof HTMLImageElement)) return;

  image.loading = 'eager';
  image.decoding = 'async';
  image.fetchPriority = 'high';

  if (!image.dataset.hdCardMediaDecodeBound && typeof image.decode === 'function') {
    image.dataset.hdCardMediaDecodeBound = 'true';
    image.decode().then(() => scheduleRefresh()).catch(() => {});
  }
}

function hasPassiveFallback(container) {
  const posterImage = window.getComputedStyle(container).getPropertyValue('--hd-card-poster-image').trim();
  return Boolean(posterImage && posterImage !== 'none' && !posterImage.includes('url("")'));
}

function markMediaState(container, image, primaryImage = null) {
  const isShopCard = container.matches(SHOP_CARD_SELECTOR);
  const mobileMode = (container.matches(CARD_SELECTOR) || isShopCard) && isMobileCardMode();
  const primaryExpected = container.matches(CARD_SELECTOR) || isShopCard;
  const isPrimaryImage = Boolean(primaryImage && image === primaryImage);

  container.toggleAttribute('data-hd-card-mobile-mode', mobileMode);

  if (!image) {
    container.dataset.hdCardMediaState = 'missing';
    container.removeAttribute('data-hd-card-media-ready');
    container.removeAttribute('data-hd-card-native-media-ready');
    container.removeAttribute('data-hd-card-primary-media-ready');
    container.toggleAttribute('data-hd-card-primary-media-missing', primaryExpected);
    container.toggleAttribute('data-hd-card-media-fallback-active', !mobileMode && hasPassiveFallback(container));
    container.setAttribute('data-hd-card-media-missing', 'true');
    return;
  }

  const containerRect = container.getBoundingClientRect();
  const imageRect = image.getBoundingClientRect();
  const imageReady = image.complete && image.naturalWidth > 1 && image.naturalHeight > 1;
  const visibleBox = containerRect.width > 1 && containerRect.height > 1 && imageRect.width > 1 && imageRect.height > 1;
  const ready = imageReady && visibleBox;
  const fallbackActive = !ready && !mobileMode && hasPassiveFallback(container);

  container.dataset.hdCardMediaState = ready ? 'ready' : image.complete ? 'missing' : 'pending';
  container.toggleAttribute('data-hd-card-media-ready', ready);
  container.toggleAttribute('data-hd-card-native-media-ready', ready);
  container.toggleAttribute('data-hd-card-primary-media-ready', primaryExpected && ready && isPrimaryImage);
  container.toggleAttribute('data-hd-card-primary-media-missing', primaryExpected && (!primaryImage || (isPrimaryImage && image.complete && !ready)));
  container.toggleAttribute('data-hd-card-media-fallback-active', fallbackActive);
  container.toggleAttribute('data-hd-card-media-missing', !ready && image.complete);

  if (!(image instanceof HTMLImageElement) || image.dataset.hdCardMediaLoadBound) return;

  image.dataset.hdCardMediaLoadBound = 'true';
  image.addEventListener('load', () => scheduleRefresh(container), { once: true });
  image.addEventListener('error', () => {
    container.dataset.hdCardMediaState = 'missing';
    container.removeAttribute('data-hd-card-media-ready');
    container.removeAttribute('data-hd-card-native-media-ready');
    container.removeAttribute('data-hd-card-primary-media-ready');
    container.toggleAttribute('data-hd-card-primary-media-missing', primaryExpected);
    container.toggleAttribute('data-hd-card-media-fallback-active', !mobileMode && hasPassiveFallback(container));
    container.setAttribute('data-hd-card-media-missing', 'true');
  });
}

function isVisibleControl(element) {
  if (!(element instanceof HTMLElement)) return false;

  const rect = element.getBoundingClientRect();
  const style = window.getComputedStyle(element);
  return (
    rect.width >= 12 &&
    rect.height >= 12 &&
    style.display !== 'none' &&
    style.visibility !== 'hidden' &&
    Number(style.opacity || 1) >= 0.9
  );
}

function markControlState(container) {
  const mobileControlsExpected = window.matchMedia('(max-width: 749px)').matches;
  const colorDots = container.querySelector(COLOR_DOTS_SELECTOR);
  const quickAddButtons = [...container.querySelectorAll(QUICK_ADD_SELECTOR)];
  const visibleQuickAdd = quickAddButtons.find(isVisibleControl);
  const colorDotsReady = isVisibleControl(colorDots);
  const quickAddReady = isVisibleControl(visibleQuickAdd);
  const hasControls = Boolean(colorDots || quickAddButtons.length);

  container.toggleAttribute('data-hd-card-color-controls-ready', colorDotsReady);
  container.toggleAttribute('data-hd-card-quick-add-ready', quickAddReady);
  container.toggleAttribute('data-hd-card-controls-ready', colorDotsReady || quickAddReady);
  container.toggleAttribute('data-hd-card-controls-missing', mobileControlsExpected && hasControls && !colorDotsReady && !quickAddReady);
}

function guardCard(card) {
  if (!(card instanceof HTMLElement)) return;

  removePdpHoverLeak(card);

  const content = card.closest('.product-card__content');
  if (content instanceof HTMLElement) {
    const galleryRatio = window.getComputedStyle(card).getPropertyValue('--gallery-aspect-ratio').trim();
    if (galleryRatio) content.style.setProperty('--hd-card-gallery-aspect-ratio', galleryRatio);
  }

  card.querySelectorAll('[data-hd-card-active-slide]').forEach((slide) => {
    slide.removeAttribute('data-hd-card-active-slide');
  });

  const activeSlide = getActiveSlide(card);
  if (activeSlide instanceof HTMLElement) {
    activeSlide.dataset.hdCardActiveSlide = 'true';
  }

  const primaryImage = getPrimaryImage(card);
  const image = primaryImage || (activeSlide || card).querySelector(IMAGE_SELECTOR);
  boostImageLoad(image);
  markMediaState(card, image, primaryImage);
  markControlState(card);
}

function guardTile(tile) {
  if (!(tile instanceof HTMLElement)) return;
  removePdpHoverLeak(tile);
  const image = tile.querySelector(SHOP_IMAGE_SELECTOR) || tile.querySelector('img');
  boostImageLoad(image);
  markMediaState(tile, image, image?.matches?.(SHOP_IMAGE_SELECTOR) ? image : null);
  markControlState(tile);
}

function refresh(root = document) {
  root.querySelectorAll(CARD_SELECTOR).forEach(guardCard);
  root.querySelectorAll(SHOP_CARD_SELECTOR).forEach(guardTile);
  root.querySelectorAll(TILE_SELECTOR).forEach(guardTile);
}

function scheduleRefresh(root = document) {
  if (refreshQueued) return;
  refreshQueued = true;

  window.requestAnimationFrame(() => {
    refreshQueued = false;
    refresh(root);
  });
}

function mutationTouchesCards(mutations) {
  return mutations.some((mutation) => {
    if (mutation.type === 'childList') {
      return [...mutation.addedNodes, ...mutation.removedNodes].some((node) => {
        if (!(node instanceof Element)) return false;
        return (
          node.matches?.(`${CARD_SELECTOR}, ${SHOP_CARD_SELECTOR}, ${TILE_SELECTOR}`) ||
          node.querySelector?.(`${CARD_SELECTOR}, ${SHOP_CARD_SELECTOR}, ${TILE_SELECTOR}`)
        );
      });
    }

    if (mutation.type === 'attributes' && mutation.target instanceof Element) {
      return Boolean(
        mutation.target.closest?.(`${CARD_SELECTOR}, ${SHOP_CARD_SELECTOR}, ${TILE_SELECTOR}`) ||
          mutation.target.matches?.('[product-grid-view], html')
      );
    }

    return false;
  });
}

function initProductCardMediaGuard() {
  refresh();

  const observer = new MutationObserver((mutations) => {
    if (mutationTouchesCards(mutations)) scheduleRefresh();
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: [
      'aria-hidden',
      'class',
      'data-hd-theme-mode',
      'hidden',
      'product-grid-view',
      'style',
    ],
  });

  document.addEventListener('shopify:section:load', (event) => refresh(event.target));
  document.addEventListener('shopify:section:select', (event) => refresh(event.target));
  document.addEventListener('shopify:block:select', (event) => refresh(event.target));
  document.addEventListener('hd:theme-mode-change', () => scheduleRefresh());
  document.addEventListener('change', (event) => {
    if (event.target instanceof HTMLInputElement && event.target.name?.includes('grid')) {
      scheduleRefresh();
    }
  });
  window.addEventListener('pageshow', () => scheduleRefresh());
  window.addEventListener('load', () => scheduleRefresh());
  window.addEventListener('resize', () => scheduleRefresh(), { passive: true });

  window.HDProductCardMediaGuard = {
    refresh,
    scheduleRefresh,
  };
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductCardMediaGuard, { once: true });
} else {
  initProductCardMediaGuard();
}
